import { samplePath } from "../SvgEditor/helpers/splitPathToRails";
import { satinRailQuality } from "./satinRailQuality";

// Monotone correspondence can match unequal-length rails without pairing
// every point solely by its percentage of the full boundary length.
export function matchRailDirections(left, right) {
  const n = left.length;
  if (n < 10 || right.length !== n) return null;
  const tangent = (rail, i) => {
    const a = rail[Math.max(0, i - 3)],
      b = rail[Math.min(n - 1, i + 3)];
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    return { x: (b.x - a.x) / len, y: (b.y - a.y) / len };
  };
  const lt = left.map((_, i) => tangent(left, i)),
    rt = right.map((_, i) => tangent(right, i));
  const widths = left.map((p, i) =>
    Math.hypot(p.x - right[i].x, p.y - right[i].y)
  );
  const scale = Math.max(...widths, 1e-8);
  const cost = (i, j) => {
    const dx = right[j].x - left[i].x,
      dy = right[j].y - left[i].y,
      w = Math.hypot(dx, dy);
    if (w < scale * 1e-6)
      return (i === 0 && j === 0) || (i === n - 1 && j === n - 1) ? 0 : 1e6;
    const x = lt[i].x + rt[j].x,
      y = lt[i].y + rt[j].y,
      len = Math.hypot(x, y) || 1;
    const skew = Math.abs(dx * x + dy * y) / (w * len);
    return skew * skew + (0.025 * w) / scale + (0.005 * Math.abs(i - j)) / n;
  };
  const scores = Array.from({ length: n }, () => Array(n).fill(Infinity));
  const previous = Array.from({ length: n }, () => Array(n).fill(null));
  scores[0][0] = 0;
  for (let i = 1; i < n; i++)
    for (let j = 1; j < n; j++) {
      for (const [di, dj] of [
        [1, 1],
        [1, 2],
        [2, 1],
      ]) {
        if (i < di || j < dj) continue;
        const value = scores[i - di][j - dj] + (cost(i, j) * (di + dj)) / 2;
        if (value < scores[i][j]) {
          scores[i][j] = value;
          previous[i][j] = [i - di, j - dj];
        }
      }
    }
  const path = [];
  let point = [n - 1, n - 1];
  while (point) {
    path.push(point);
    point = previous[point[0]][point[1]];
  }
  path.reverse();
  if (path[0][0] !== 0 || path[0][1] !== 0) return null;
  return path.map(([i, j]) => ({ left: left[i], right: right[j] }));
}

export function improveSatinGuides(pair, containsGuides) {
  const left = samplePath(pair.rail0, 80),
    right = samplePath(pair.rail1, 80);
  const matched = matchRailDirections(left, right);
  if (!matched) return pair;
  const selected = [];
  for (let k = 1; k <= 18; k++)
    selected.push(matched[Math.round((k * (matched.length - 1)) / 19)]);
  const a = selected.map((p) => p.left),
    b = selected.map((p) => p.right);
  const quality = satinRailQuality(a, b),
    baseline = satinRailQuality(left, right);
  if (quality === null || baseline === null || quality >= baseline - 0.005)
    return pair;
  const rungs = selected.map(
    ({ left: a, right: b }) => `M${a.x},${a.y} L${b.x},${b.y}`
  );
  const improved = {
    ...pair,
    rungs,
    directionMethod: "monotone-side-correspondence",
  };
  return containsGuides(improved) ? improved : pair;
}
